import React, { useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { THEME_COLORS } from '../../shared/constants/theme';

const PMS_COLOR = '#4ce1b6';
const CM_COLOR = '#c9843a';

const planColors = (plan) => {
  const isPms = (plan?.is_pms_enabled ?? true) === true;
  const isCm = (plan?.is_channel_manager_enabled ?? true) === true;
  if (isCm && !isPms) return { accent: CM_COLOR, isCmFill: true };
  if (isPms && !isCm) return { accent: PMS_COLOR, isCmFill: false };
  return { accent: PMS_COLOR, isCmFill: false };
};

const moveItem = (items, fromId, toId) => {
  const next = [...items];
  const fromIndex = next.findIndex((item) => String(item.id) === String(fromId));
  const toIndex = next.findIndex((item) => String(item.id) === String(toId));
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return items;
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const RatePlanBoard = ({
  ratePlans,
  roomTypes,
  isDark,
  isMobileLayout,
  deletingId,
  savingOrder,
  onEdit,
  onDelete,
  onReorder,
}) => {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const roomNameById = useMemo(() => {
    const map = {};
    (roomTypes || []).forEach((room) => {
      map[room.id] = room.name;
    });
    return map;
  }, [roomTypes]);

  const surface = isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff';
  const border = isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08);
  const muted = isDark ? '#94a3b8' : '#64748b';

  const handleDrop = (targetId) => {
    if (!dragId || String(dragId) === String(targetId)) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const next = moveItem(ratePlans, dragId, targetId);
    setDragId(null);
    setOverId(null);
    if (next !== ratePlans) onReorder(next.map((plan) => plan.id));
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: muted, letterSpacing: 0.4, mb: 1.25 }}>
        Drag the handle to set display order. Sort order saves automatically.
      </Typography>
      <Grid container spacing={isMobileLayout ? 1.25 : 1.75}>
        {ratePlans.map((plan, index) => {
          const { accent, isCmFill } = planColors(plan);
          const isDragging = String(dragId) === String(plan.id);
          const isOver = String(overId) === String(plan.id) && !isDragging;
          const roomLabels = plan.applies_to_all_room_types || !plan.room_type_ids?.length
            ? ['All rooms']
            : plan.room_type_ids.map((id) => roomNameById[id] || `Room ${id}`);
          const cardBorder = isOver
            ? alpha(accent, 0.45)
            : isCmFill
              ? alpha(CM_COLOR, isDark ? 0.2 : 0.14)
              : border;
          const cardFill = isCmFill
            ? {
                backgroundColor: surface,
                backgroundImage: `linear-gradient(180deg, ${alpha(CM_COLOR, isDark ? 0.09 : 0.06)} 0%, ${alpha(CM_COLOR, isDark ? 0.03 : 0.02)} 100%)`,
              }
            : {
                backgroundColor: surface,
              };

          return (
            <Grid item xs={12} sm={6} md={6} lg={4} key={plan.id}>
              <Paper
                elevation={0}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (String(overId) !== String(plan.id)) setOverId(plan.id);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  handleDrop(plan.id);
                }}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2.5,
                  ...cardFill,
                  border: `1px solid ${cardBorder}`,
                  boxShadow: isDragging
                    ? `0 16px 36px ${alpha('#0f172a', isDark ? 0.5 : 0.16)}`
                    : isOver
                      ? `0 0 0 1px ${alpha(accent, 0.28)}`
                      : `0 8px 22px ${alpha('#0f172a', isDark ? 0.28 : 0.06)}`,
                  opacity: isDragging ? 0.5 : 1,
                  transform: isOver ? 'scale(0.985)' : 'none',
                  cursor: savingOrder ? 'progress' : 'default',
                  transition: 'box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease',
                  '&:hover': {
                    borderColor: alpha(accent, 0.35),
                    boxShadow: `0 12px 24px ${alpha('#0f172a', isDark ? 0.32 : 0.08)}`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ p: 1.85, pt: 1.55, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        draggable={!savingOrder}
                        onDragStart={(event) => {
                          setDragId(plan.id);
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', String(plan.id));
                        }}
                        onDragEnd={() => {
                          setDragId(null);
                          setOverId(null);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: muted,
                          cursor: savingOrder ? 'progress' : 'grab',
                          '&:active': { cursor: 'grabbing' },
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 22, mt: 0.35 }} />
                      </Box>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: 13,
                          fontWeight: 800,
                          flexShrink: 0,
                          bgcolor: alpha(accent, isDark ? 0.22 : 0.14),
                          color: accent,
                        }}
                      >
                        {String(plan.code || plan.name || '?').slice(0, 2).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 800,
                            lineHeight: 1.3,
                            color: isDark ? '#f8fafc' : '#0f172a',
                            wordBreak: 'break-word',
                          }}
                        >
                          {plan.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            color: muted,
                            wordBreak: 'break-all',
                          }}
                        >
                          {plan.code}
                        </Typography>
                      </Box>
                    </Stack>
                    <Chip
                      label={String(index + 1).padStart(2, '0')}
                      size="small"
                      sx={{
                        height: 24,
                        minWidth: 36,
                        fontSize: 11,
                        fontWeight: 800,
                        flexShrink: 0,
                        bgcolor: alpha(accent, isDark ? 0.2 : 0.12),
                        color: accent,
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
                    {plan.meal_plan_display ? (
                      <Chip
                        label={plan.meal_plan_display}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: isDark ? alpha('#fff', 0.06) : '#f1f5f9',
                          color: muted,
                        }}
                      />
                    ) : null}
                    {(plan.is_pms_enabled ?? true) && (
                      <Chip
                        label="PMS"
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: 11,
                          fontWeight: 800,
                          bgcolor: isDark ? 'rgba(76, 225, 182, 0.16)' : 'rgba(16, 185, 129, 0.12)',
                          color: isDark ? '#4ce1b6' : '#047857',
                        }}
                      />
                    )}
                    {(plan.is_channel_manager_enabled ?? true) && (
                      <Chip
                        label="CM"
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: 11,
                          fontWeight: 800,
                          bgcolor: isDark ? alpha(CM_COLOR, 0.14) : alpha(CM_COLOR, 0.1),
                          color: isDark ? '#e0b07a' : '#9a6700',
                        }}
                      />
                    )}
                    <Chip
                      label={plan.is_refundable ? 'Refundable' : 'Non-refundable'}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: plan.is_refundable
                          ? alpha('#22c55e', isDark ? 0.16 : 0.12)
                          : alpha('#ef4444', isDark ? 0.16 : 0.1),
                        color: plan.is_refundable ? '#16a34a' : '#dc2626',
                      }}
                    />
                  </Stack>

                  {plan.description ? (
                    <Typography
                      sx={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: muted,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {plan.description}
                    </Typography>
                  ) : null}

                  {plan.cancellation_policy ? (
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        lineHeight: 1.5,
                        color: muted,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      Cancellation: {plan.cancellation_policy}
                    </Typography>
                  ) : null}

                  <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
                    {roomLabels.map((label) => (
                      <Chip
                        key={label}
                        label={label}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: isDark ? alpha('#fff', 0.05) : '#f1f5f9',
                          color: isDark ? '#e2e8f0' : '#334155',
                        }}
                      />
                    ))}
                  </Stack>

                  <Stack direction="row" justifyContent="flex-end" spacing={0.25} sx={{ mt: 0.25 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(plan)}
                        sx={{ color: isDark ? '#4ce1b6' : '#0f766e' }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(plan)}
                        disabled={deletingId === plan.id}
                        sx={{ color: isDark ? '#fb7185' : '#dc2626' }}
                      >
                        {deletingId === plan.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default RatePlanBoard;
